import { useCallback, useEffect, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { useRootStore } from '../Store/useRootStore';
import { MediaEvent, submitMediaEvents } from '../Model/Analytics/MediaAnalyticsApi';

export type MediaAnalyticsConfig = {
  contentId: string;
  contentType: 'video' | 'audio';
  mediaType: string; // 'webinar', 'meditation', 'workout', 'breathing', 'article'
  contentName?: string;
};

type SessionState = {
  sessionId: string;
  started: boolean;
  totalDuration: number;
  watchedDuration: number;
  playCount: number;
  pauseCount: number;
  seekCount: number;
  bufferCount: number;
  milestonesReached: Set<number>;
  events: MediaEvent[];
  lastPosition: number;
  seekFromPosition: number | null;
  bufferStartTime: number | null;
  playStartTime: number | null;
  isMobile: boolean;
};

const MILESTONES = [25, 50, 75, 100];

function getDevice(): string {
  return window.matchMedia('(max-width: 768px)').matches ? 'mobile' : 'desktop';
}

export function useMediaAnalytics(config?: MediaAnalyticsConfig) {
  const { analytics } = useRootStore();
  const stateRef = useRef<SessionState | null>(null);

  // Initialize session state on mount
  useEffect(() => {
    if (!config) return;

    stateRef.current = {
      sessionId: uuid(),
      started: false,
      totalDuration: 0,
      watchedDuration: 0,
      playCount: 0,
      pauseCount: 0,
      seekCount: 0,
      bufferCount: 0,
      milestonesReached: new Set(),
      events: [],
      lastPosition: 0,
      seekFromPosition: null,
      bufferStartTime: null,
      playStartTime: null,
      isMobile: getDevice() === 'mobile',
    };

    // Cleanup: fire session end + submit events
    return () => {
      const s = stateRef.current;
      if (!s || !s.started) return;

      // Accumulate any in-progress watch time
      if (s.playStartTime !== null) {
        s.watchedDuration += (Date.now() - s.playStartTime) / 1000;
        s.playStartTime = null;
      }

      const percentage = s.totalDuration > 0 ? Math.min(100, Math.round((s.watchedDuration / s.totalDuration) * 100)) : 0;

      const endEvent = makeEvent(s, config, 'media_session_end', {
        watchedDuration: Math.round(s.watchedDuration),
        totalDuration: Math.round(s.totalDuration),
        percentage,
        playCount: s.playCount,
        pauseCount: s.pauseCount,
        seekCount: s.seekCount,
        bufferCount: s.bufferCount,
        device: getDevice(),
      });
      s.events.push(endEvent);
      fireToFirebase(analytics, endEvent);

      // Batch POST to backend - use sendBeacon as fallback
      const payload = JSON.stringify({ events: s.events });
      if (navigator.sendBeacon) {
        navigator.sendBeacon('/api/v1/analytics/media-event', new Blob([payload], { type: 'application/json' }));
      } else {
        submitMediaEvents(s.events).catch(() => {});
      }
    };
  }, [config?.contentId]); // Re-init if contentId changes

  const onPlay = useCallback(() => {
    const s = stateRef.current;
    if (!s || !config) return;

    s.playCount++;
    s.playStartTime = Date.now();

    if (!s.started) {
      s.started = true;
      const startEvent = makeEvent(s, config, 'media_session_start', {
        contentName: config.contentName?.substring(0, 100),
        totalDuration: Math.round(s.totalDuration),
        device: getDevice(),
      });
      s.events.push(startEvent);
      fireToFirebase(analytics, startEvent);
    }

    const playEvent = makeEvent(s, config, 'media_play', {
      position: Math.round(s.lastPosition),
    });
    s.events.push(playEvent);
    fireToFirebase(analytics, playEvent);
  }, [config, analytics]);

  const onPause = useCallback(() => {
    const s = stateRef.current;
    if (!s || !config) return;

    s.pauseCount++;
    if (s.playStartTime !== null) {
      s.watchedDuration += (Date.now() - s.playStartTime) / 1000;
      s.playStartTime = null;
    }

    const pauseEvent = makeEvent(s, config, 'media_pause', {
      position: Math.round(s.lastPosition),
      watchedSoFar: Math.round(s.watchedDuration),
    });
    s.events.push(pauseEvent);
    fireToFirebase(analytics, pauseEvent);
  }, [config, analytics]);

  const onSeeking = useCallback(() => {
    const s = stateRef.current;
    if (!s) return;
    s.seekFromPosition = s.lastPosition;
  }, []);

  const onSeeked = useCallback(() => {
    const s = stateRef.current;
    if (!s || !config || s.seekFromPosition === null) return;

    const fromPos = Math.round(s.seekFromPosition);
    const toPos = Math.round(s.lastPosition);

    // Only log if the seek was meaningful (more than 2 seconds)
    if (Math.abs(toPos - fromPos) > 2) {
      s.seekCount++;
      const seekEvent = makeEvent(s, config, 'media_seek', {
        fromPosition: fromPos,
        toPosition: toPos,
      });
      s.events.push(seekEvent);
      fireToFirebase(analytics, seekEvent);
    }
    s.seekFromPosition = null;
  }, [config, analytics]);

  // onWaiting = buffering started
  const onWaiting = useCallback(() => {
    const s = stateRef.current;
    if (!s) return;
    s.bufferStartTime = Date.now();
  }, []);

  // onPlaying = buffering resolved (also fires on normal play, so check bufferStartTime)
  const onPlaying = useCallback(() => {
    const s = stateRef.current;
    if (!s || !config || s.bufferStartTime === null) return;

    const bufferDurationMs = Date.now() - s.bufferStartTime;
    s.bufferStartTime = null;

    // Only log meaningful buffering (> 200ms)
    if (bufferDurationMs > 200) {
      s.bufferCount++;
      const bufferEvent = makeEvent(s, config, 'media_buffer', {
        position: Math.round(s.lastPosition),
        bufferDurationMs,
      });
      s.events.push(bufferEvent);
      fireToFirebase(analytics, bufferEvent);
    }
  }, [config, analytics]);

  const onTimeUpdate = useCallback(
    (currentTime: number, duration: number) => {
      const s = stateRef.current;
      if (!s || !config) return;

      s.lastPosition = currentTime;
      if (duration > 0) {
        s.totalDuration = duration;
      }

      // Check milestones
      if (s.totalDuration > 0) {
        const pct = (currentTime / s.totalDuration) * 100;
        for (const milestone of MILESTONES) {
          if (pct >= milestone && !s.milestonesReached.has(milestone)) {
            s.milestonesReached.add(milestone);
            const milestoneEvent = makeEvent(s, config, 'media_milestone', {
              milestone,
              position: Math.round(currentTime),
            });
            s.events.push(milestoneEvent);
            fireToFirebase(analytics, milestoneEvent);
          }
        }
      }
    },
    [config, analytics],
  );

  const onEnded = useCallback(() => {
    const s = stateRef.current;
    if (!s || !config) return;

    if (s.playStartTime !== null) {
      s.watchedDuration += (Date.now() - s.playStartTime) / 1000;
      s.playStartTime = null;
    }

    // 100% milestone if not already reached
    if (!s.milestonesReached.has(100)) {
      s.milestonesReached.add(100);
      const milestoneEvent = makeEvent(s, config, 'media_milestone', {
        milestone: 100,
        position: Math.round(s.totalDuration),
      });
      s.events.push(milestoneEvent);
      fireToFirebase(analytics, milestoneEvent);
    }
  }, [config, analytics]);

  const onRateChange = useCallback(
    (fromSpeed: number, toSpeed: number) => {
      const s = stateRef.current;
      if (!s || !config || fromSpeed === toSpeed) return;

      const speedEvent = makeEvent(s, config, 'media_speed_change', {
        fromSpeed,
        toSpeed,
      });
      s.events.push(speedEvent);
      fireToFirebase(analytics, speedEvent);
    },
    [config, analytics],
  );

  const onQualityChange = useCallback(
    (fromQuality: string, toQuality: string) => {
      const s = stateRef.current;
      if (!s || !config) return;

      const qualityEvent = makeEvent(s, config, 'media_quality_change', {
        fromQuality,
        toQuality,
      });
      s.events.push(qualityEvent);
      fireToFirebase(analytics, qualityEvent);
    },
    [config, analytics],
  );

  const onLanguageChange = useCallback(
    (fromLang: string, toLang: string) => {
      const s = stateRef.current;
      if (!s || !config) return;

      const langEvent = makeEvent(s, config, 'media_language_change', {
        fromLang,
        toLang,
      });
      s.events.push(langEvent);
      fireToFirebase(analytics, langEvent);
    },
    [config, analytics],
  );

  // If no config, return no-op handlers
  if (!config) {
    return {
      onPlay: noop,
      onPause: noop,
      onSeeking: noop,
      onSeeked: noop,
      onWaiting: noop,
      onPlaying: noop,
      onTimeUpdate: noopTimeUpdate,
      onEnded: noop,
      onRateChange: noopRateChange,
      onQualityChange: noopStringPair,
      onLanguageChange: noopStringPair,
    };
  }

  return {
    onPlay,
    onPause,
    onSeeking,
    onSeeked,
    onWaiting,
    onPlaying,
    onTimeUpdate,
    onEnded,
    onRateChange,
    onQualityChange,
    onLanguageChange,
  };
}

// --- Helpers ---

function makeEvent(
  state: SessionState,
  config: MediaAnalyticsConfig,
  event: string,
  params: Record<string, string | number | boolean | undefined>,
): MediaEvent {
  return {
    event,
    sessionId: state.sessionId,
    contentId: config.contentId,
    contentType: config.contentType,
    mediaType: config.mediaType,
    timestamp: Date.now(),
    ...params,
  };
}

function fireToFirebase(analytics: { logEvent: (name: string, params?: Record<string, any>) => void }, event: MediaEvent) {
  const { event: eventName, ...params } = event;
  analytics.logEvent(eventName, params);
}

const noop = () => {};
const noopTimeUpdate = (_currentTime: number, _duration: number) => {};
const noopRateChange = (_from: number, _to: number) => {};
const noopStringPair = (_from: string, _to: string) => {};
